/* eslint-disable react-hooks/exhaustive-deps */
import { useToast } from '@welbe/web-design-system';
import { Auth } from 'aws-amplify';
import Analytics from 'helpers/analytics';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from 'services/api';
import { getMeInfo } from 'services/services';
import { createContext } from 'use-context-selector';
import { errorsCode } from 'Views/Login/Login.enums';

type SignInProps = {
	username: string;
	password: string;
};

type NewPasswordProps = {
	username: string;
	password: string;
	confirmPassword: string;
};

type PasswordRecoverProps = {
	email: string;
};

interface IAuthenticationContextData {
	handleSignIn: (payload: SignInProps) => Promise<void>;
	handleSignOut: () => void;
	handleNewPassword: (payload: NewPasswordProps) => Promise<void>;
	handlePasswordRecover: (payload: PasswordRecoverProps) => Promise<boolean>;
	handleStorageAuth: (payload: any) => void;
	cognitoUser: any;
	isAuthenticated: boolean | null;
	isLoading: boolean;
	errorInfo: string;
}

type AuthenticationProviderProps = {
	children: ReactNode;
};

const AuthenticationContext = createContext({} as IAuthenticationContextData);

const AuthProvider = ({
	children,
}: AuthenticationProviderProps): JSX.Element => {
	const { toast } = useToast();
	const { getSessionStorage, setSessionStorage, removeKeySessionStorage } =
		useSessionStorage();

	const handleToast = (message?: string) => {
		toast.error(
			message ||
				'Hubo un error al intentar procesar esta operación. Inténtalo de nuevo más tarde.',
		);
	};

	const emptyIsAuthenticated = () => {
		const userStorage = getSessionStorage('isAuthenticated');
		const storageKeys = Object.keys(sessionStorage);
		const cognitoKeyExists = storageKeys.some((key) => key.includes('Cognito'));

		if (userStorage && cognitoKeyExists) {
			const userParse = JSON.parse(userStorage);

			return userParse;
		}

		return null;
	};

	const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(
		emptyIsAuthenticated(),
	);
	const [isLoading, setLoading] = useState(false);
	const [errorInfo, setErrorInfo] = useState('');
	const [cognitoUser, setCognitoUser] = useState(null);
	const navigate = useNavigate();

	const clearsessionStorage = () => {
		sessionStorage.clear();
		localStorage.clear();
	};

	const requestNewPassword = async (user: any, username: string) => {
		setSessionStorage('user', JSON.stringify(user.attributes));
		setCognitoUser(user);
		setSessionStorage('username', username);

		navigate('/new-password');
	};

	const request2FA = async (user: any, username: string) => {
		setCognitoUser(user);
		navigate(
			`/verification-code?token=${window.btoa(
				JSON.stringify({ email: username, type: '2fa' }),
			)}`,
		);
	};

	const handleStorageAuth = async (user: any) => {
		const { jwtToken } = user.signInUserSession.idToken;
		setSessionStorage('idToken', JSON.stringify(jwtToken));
		setSessionStorage('isAuthenticated', 'true');
		setIsAuthenticated(true);
		return;
	};

	const handleSignIn = useCallback(
		async ({ username, password }: SignInProps) => {
			removeKeySessionStorage('me');
			setErrorInfo('');
			setLoading(true);
			try {
				Auth.configure({
					storage: sessionStorage,
				});
				const user = await Auth.signIn(username, password);

				if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
					requestNewPassword(user, username);
					return;
				}

				if (user.authenticationFlowType === 'CUSTOM_AUTH') {
					request2FA(user, username);
					return;
				}
				const { data } = await getMeInfo();
				setSessionStorage('me', JSON.stringify(data.content));

				await handleStorageAuth(user);

				Analytics.track('login', 'User Login', {
					username,
				});

				navigate('/');
			} catch (error) {
				const { code } = error;
				if (code === 'UserNotConfirmedException') {
					navigate(
						`/verification-code?token=${window.btoa(
							JSON.stringify({
								email: username,
								type: 'confirmation',
							}),
						)}`,
					);
				}

				Analytics.track('login', 'Error User Login', {
					username,
				});

				if (code) {
					setErrorInfo(errorsCode[code]);
					return;
				}
				handleToast(
					'Hubo un error al intentar iniciar sesión en el sistema. Vuelve a intentarlo más tarde.',
				);
			} finally {
				setLoading(false);
			}
		},
		[cognitoUser],
	);

	const handleSignOut = useCallback(async () => {
		await Auth.signOut();
		await clearsessionStorage();
		setIsAuthenticated(emptyIsAuthenticated());
	}, []);

	const handleNewPassword = useCallback(
		async ({ username, password, confirmPassword }: NewPasswordProps) => {
			try {
				setErrorInfo('');
				setLoading(true);

				if (password !== confirmPassword) {
					setErrorInfo('Las contraseñas deben ser las mismas');
					return;
				}

				await Auth.completeNewPassword(cognitoUser, password);
				const user = await Auth.signIn(username, password);
				setSessionStorage('user', JSON.stringify(user.attributes));
				setSessionStorage(
					'idToken',
					JSON.stringify(user.signInUserSession.idToken.jwtToken),
				);
				setSessionStorage('isAuthenticated', 'true');
				setIsAuthenticated(true);
				navigate('/');
			} catch ({ code }) {
				if (errorsCode[code]) {
					setErrorInfo(errorsCode[code]);
					return;
				}
				handleToast(
					'Hubo un error al intentar cambiar la contraseña. Vuelve a intentarlo más tarde.',
				);
			} finally {
				setLoading(false);
			}
		},
		[cognitoUser],
	);

	const handlePasswordRecover = useCallback(
		async ({ email }: PasswordRecoverProps): Promise<boolean> => {
			try {
				setErrorInfo('');
				setLoading(true);

				await api.post('/users/reset-password', {
					email,
				});

				await Auth.forgotPassword(email);
				return true;
			} catch (error: any) {
				if (error?.response?.status === 404) {
					setErrorInfo('El correo no se encuentra en nuestro registro');
				}
				return false;
			} finally {
				setLoading(false);
			}
		},
		[],
	);

	return (
		<AuthenticationContext.Provider
			value={{
				handleSignIn,
				handleSignOut,
				handleNewPassword,
				handlePasswordRecover,
				handleStorageAuth,
				cognitoUser,
				isAuthenticated,
				isLoading,
				errorInfo,
			}}
		>
			{children}
		</AuthenticationContext.Provider>
	);
};

export { AuthenticationContext, AuthProvider };
